import { useCallback, useEffect, useRef, useState } from 'react';
import {
  FLOWCHART_UNDO_PERSIST_MS,
  FLOWCHART_UNDO_RECORD_DEBOUNCE_MS,
} from './constants';
import { cloneCanvasSnapshot } from './cloneCanvasSnapshot';
import { createFlowchartUndoStack } from './flowchartUndoStack';

export function useFlowchartUndoController({
  enabled = false,
  versionKey = null,
  isLayouting = false,
  getIsUserMutated,
  getIdentifiedRisks,
  nodes,
  edges,
  applySnapshot,
  onAfterUndoRedo,
  onRequestPersistFlush,
  gateRefs = null,
}) {
  const stackRef = useRef(createFlowchartUndoStack());
  const baselineReadyForKeyRef = useRef(null);
  // After versionKey reset, wait for one layout pass before baseline (avoids
  // published→draft locking the previous version’s canvas as baseline).
  // Safe with version-number-only key: number change always re-layouts; id-only save does not reset.
  const mustWaitForLayoutRef = useRef(false);
  const layoutSeenForKeyRef = useRef(null);
  const restoringRef = useRef(false);
  // After Z/Y, hold record until canvas differs so redo stays available.
  const holdRecordAfterRestoreRef = useRef(false);
  // Absorb risks/controls node sync that runs right after restore (must not clear redo).
  const restoreEchoUntilRef = useRef(0);
  // Short absorb for side-panel attach/detach risk stamps during pause (must not clear redo).
  const liveAbsorbUntilRef = useRef(0);
  const recordTimerRef = useRef(null);
  const persistTimerRef = useRef(null);
  const lastRecordedRef = useRef(null);

  const persistPausedRef = useRef(false);
  const suppressGetRefreshRef = useRef(false);
  const ignorePersistResultRef = useRef(false);
  const gateRefsRef = useRef(gateRefs);

  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [persistPaused, setPersistPaused] = useState(false);
  const [persistEndsAt, setPersistEndsAt] = useState(null);

  const enabledRef = useRef(enabled);
  const applySnapshotRef = useRef(applySnapshot);
  const onAfterUndoRedoRef = useRef(onAfterUndoRedo);
  const onRequestPersistFlushRef = useRef(onRequestPersistFlush);
  const getIsUserMutatedRef = useRef(getIsUserMutated);
  const getIdentifiedRisksRef = useRef(getIdentifiedRisks);
  const nodesRef = useRef(nodes);
  const edgesRef = useRef(edges);

  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { applySnapshotRef.current = applySnapshot; }, [applySnapshot]);
  useEffect(() => { onAfterUndoRedoRef.current = onAfterUndoRedo; }, [onAfterUndoRedo]);
  useEffect(() => { onRequestPersistFlushRef.current = onRequestPersistFlush; }, [onRequestPersistFlush]);
  useEffect(() => { getIsUserMutatedRef.current = getIsUserMutated; }, [getIsUserMutated]);
  useEffect(() => { getIdentifiedRisksRef.current = getIdentifiedRisks; }, [getIdentifiedRisks]);
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { edgesRef.current = edges; }, [edges]);
  useEffect(() => { gateRefsRef.current = gateRefs; }, [gateRefs]);

  const syncFlags = useCallback(() => {
    const stack = stackRef.current;
    setCanUndo(stack.canUndo());
    setCanRedo(stack.canRedo());
  }, []);

  const clearPersistTimer = useCallback(() => {
    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current);
      persistTimerRef.current = null;
    }
  }, []);

  const clearRecordTimer = useCallback(() => {
    if (recordTimerRef.current) {
      clearTimeout(recordTimerRef.current);
      recordTimerRef.current = null;
    }
  }, []);

  const setWindowFlags = useCallback((active) => {
    persistPausedRef.current = active;
    suppressGetRefreshRef.current = active;
    ignorePersistResultRef.current = active;
    const host = gateRefsRef.current;
    if (host?.persistPausedRef) host.persistPausedRef.current = active;
    if (host?.suppressGetRefreshRef) host.suppressGetRefreshRef.current = active;
    if (host?.ignorePersistResultRef) host.ignorePersistResultRef.current = active;
    setPersistPaused(active);
    if (!active) setPersistEndsAt(null);
  }, []);

  const endPersistWindow = useCallback(() => {
    clearPersistTimer();
    setWindowFlags(false);
  }, [clearPersistTimer, setWindowFlags]);

  const startOrResetPersistWindow = useCallback(() => {
    clearPersistTimer();
    setWindowFlags(true);
    setPersistEndsAt(Date.now() + FLOWCHART_UNDO_PERSIST_MS);
    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null;
      setWindowFlags(false);
      onRequestPersistFlushRef.current?.();
    }, FLOWCHART_UNDO_PERSIST_MS);
  }, [clearPersistTimer, setWindowFlags]);

  const resetForVersion = useCallback(() => {
    clearRecordTimer();
    clearPersistTimer();
    setWindowFlags(false);
    stackRef.current.clear();
    baselineReadyForKeyRef.current = null;
    mustWaitForLayoutRef.current = true;
    layoutSeenForKeyRef.current = null;
    lastRecordedRef.current = null;
    restoringRef.current = false;
    holdRecordAfterRestoreRef.current = false;
    restoreEchoUntilRef.current = 0;
    liveAbsorbUntilRef.current = 0;
    syncFlags();
  }, [clearPersistTimer, clearRecordTimer, setWindowFlags, syncFlags]);

  useEffect(() => {
    resetForVersion();
  }, [versionKey, resetForVersion]);

  useEffect(() => {
    if (!enabled) {
      resetForVersion();
    }
  }, [enabled, resetForVersion]);

  useEffect(() => () => {
    clearRecordTimer();
    clearPersistTimer();
  }, [clearPersistTimer, clearRecordTimer]);

  const snapshotsEqual = useCallback((a, b) => {
    if (!a || !b) return false;
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return false;
    }
  }, []);

  const buildSnapshot = useCallback((nextNodes, nextEdges, identifiedRisksOverride) => {
    const risks = identifiedRisksOverride !== undefined
      ? identifiedRisksOverride
      : (getIdentifiedRisksRef.current?.() ?? []);
    return cloneCanvasSnapshot(nextNodes, nextEdges, risks);
  }, []);

  const commitSnapshot = useCallback((nextNodes, nextEdges, identifiedRisksOverride) => {
    if (!enabledRef.current) return;
    if (restoringRef.current) return;
    const snapshot = buildSnapshot(nextNodes, nextEdges, identifiedRisksOverride);
    if (lastRecordedRef.current && snapshotsEqual(lastRecordedRef.current, snapshot)) {
      return;
    }
    holdRecordAfterRestoreRef.current = false;
    const stack = stackRef.current;
    if (!stack.hasPresent()) {
      stack.reset(snapshot);
    } else {
      stack.commit(snapshot);
    }
    lastRecordedRef.current = snapshot;
    syncFlags();
  }, [buildSnapshot, snapshotsEqual, syncFlags]);

  const scheduleRecord = useCallback((nextNodes, nextEdges) => {
    if (!enabledRef.current) return;
    if (restoringRef.current) return;
    const now = Date.now();
    const inRestoreEcho =
      holdRecordAfterRestoreRef.current && now < restoreEchoUntilRef.current;
    const inLiveAbsorb = now < liveAbsorbUntilRef.current;
    if (holdRecordAfterRestoreRef.current || inLiveAbsorb) {
      const probe = buildSnapshot(nextNodes, nextEdges);
      if (lastRecordedRef.current && snapshotsEqual(lastRecordedRef.current, probe)) {
        return;
      }
      // Right after Z/Y, risks→nodes / mappedControls sync rewrites nodes. Catch up
      // without commit so Identify Risks → Z → Y keeps redo. Past the echo window,
      // a real edit falls through and commits (clears future) as before.
      // liveAbsorb: side-panel attach/detach during pause stamps risks without clearing redo.
      if ((inRestoreEcho || inLiveAbsorb) && lastRecordedRef.current) {
        lastRecordedRef.current = probe;
        stackRef.current.replacePresent(probe);
        return;
      }
    }
    clearRecordTimer();
    recordTimerRef.current = setTimeout(() => {
      recordTimerRef.current = null;
      commitSnapshot(nextNodes, nextEdges);
    }, FLOWCHART_UNDO_RECORD_DEBOUNCE_MS);
  }, [buildSnapshot, clearRecordTimer, commitSnapshot, snapshotsEqual]);

  // Absorb upcoming node rewrites into present without commit (keeps redo).
  const beginLiveSnapshotAbsorb = useCallback((ms = 400) => {
    liveAbsorbUntilRef.current = Date.now() + Math.max(0, Number(ms) || 0);
  }, []);

  const recordCanvasNow = useCallback((nextNodes, nextEdges, identifiedRisksOverride) => {
    if (!enabledRef.current) return;
    if (restoringRef.current) return;
    clearRecordTimer();
    const n = nextNodes ?? nodesRef.current;
    const e = nextEdges ?? edgesRef.current;
    holdRecordAfterRestoreRef.current = false;
    commitSnapshot(n, e, identifiedRisksOverride);
  }, [clearRecordTimer, commitSnapshot]);

  // Track layout even before undo is enabled — otherwise layout can finish while
  // still published, then draft enables with no new layoutSeen and baseline never waits.
  useEffect(() => {
    if (versionKey == null) return;
    if (isLayouting) {
      layoutSeenForKeyRef.current = versionKey;
    }
  }, [isLayouting, versionKey]);

  // Baseline only after layout has run for this versionKey (settled draft, not prior version).
  useEffect(() => {
    if (!enabled || isLayouting) return;
    if (versionKey == null) return;
    if (baselineReadyForKeyRef.current === versionKey) return;
    if (!Array.isArray(nodes) || nodes.length === 0) return;
    if (mustWaitForLayoutRef.current && layoutSeenForKeyRef.current !== versionKey) {
      return;
    }

    const baseline = buildSnapshot(nodes, edges);
    stackRef.current.reset(baseline);
    lastRecordedRef.current = baseline;
    baselineReadyForKeyRef.current = versionKey;
    mustWaitForLayoutRef.current = false;
    syncFlags();
  }, [enabled, isLayouting, versionKey, nodes, edges, buildSnapshot, syncFlags]);

  useEffect(() => {
    if (!enabled || isLayouting) return;
    if (restoringRef.current) return;
    if (baselineReadyForKeyRef.current !== versionKey) return;
    if (!getIsUserMutatedRef.current?.()) return;
    scheduleRecord(nodes, edges);
  }, [enabled, isLayouting, versionKey, nodes, edges, scheduleRecord]);

  const applyAndEnterWindow = useCallback((snapshot) => {
    if (!snapshot) return false;
    restoringRef.current = true;
    holdRecordAfterRestoreRef.current = true;
    // Cover risks→nodes + mappedControls effects after restore (debounce is 120ms).
    restoreEchoUntilRef.current = Date.now() + 400;
    clearRecordTimer();
    applySnapshotRef.current?.(snapshot);
    // Use post-apply refs — apply may stamp identifiedRisks onto node.data so
    // lastRecorded matches live canvas (avoids echo commit clearing redo).
    lastRecordedRef.current = cloneCanvasSnapshot(
      nodesRef.current,
      edgesRef.current,
      snapshot.identifiedRisks
    );
    stackRef.current.replacePresent(lastRecordedRef.current);
    onAfterUndoRedoRef.current?.();
    startOrResetPersistWindow();
    queueMicrotask(() => {
      restoringRef.current = false;
    });
    syncFlags();
    return true;
  }, [clearRecordTimer, startOrResetPersistWindow, syncFlags]);

  const undo = useCallback(() => {
    if (!enabledRef.current) return false;
    const live = buildSnapshot(nodesRef.current, edgesRef.current);
    const stack = stackRef.current;
    if (stack.hasPresent()) {
      stack.replacePresent(live);
    }
    const prev = stack.undo();
    return applyAndEnterWindow(prev);
  }, [applyAndEnterWindow, buildSnapshot]);

  const redo = useCallback(() => {
    if (!enabledRef.current) return false;
    const live = buildSnapshot(nodesRef.current, edgesRef.current);
    const stack = stackRef.current;
    if (stack.hasPresent()) {
      stack.replacePresent(live);
    }
    const next = stack.redo();
    return applyAndEnterWindow(next);
  }, [applyAndEnterWindow, buildSnapshot]);

  const notifyPersistCaughtUp = useCallback(() => {
    endPersistWindow();
    clearRecordTimer();
    // Commit pending ~120ms record first so fast save does not wipe undo past.
    commitSnapshot(nodesRef.current, edgesRef.current);
    const live = buildSnapshot(nodesRef.current, edgesRef.current);
    stackRef.current.replacePresent(live);
    lastRecordedRef.current = live;
    syncFlags();
  }, [buildSnapshot, clearRecordTimer, commitSnapshot, endPersistWindow, syncFlags]);

  const requestFlushNow = useCallback(async () => {
    if (!persistPausedRef.current) return true;
    clearPersistTimer();
    setWindowFlags(false);
    try {
      const result = await onRequestPersistFlushRef.current?.();
      return result !== false;
    } catch {
      return false;
    }
  }, [clearPersistTimer, setWindowFlags]);

  useEffect(() => {
    if (!enabled) return undefined;

    const onKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
        return;
      }
      const mod = event.ctrlKey || event.metaKey;
      if (!mod) return;

      const key = event.key?.toLowerCase?.() || '';
      if (key === 'z' && !event.shiftKey) {
        if (!stackRef.current.canUndo()) return;
        event.preventDefault();
        undo();
        return;
      }
      if (key === 'y' || (key === 'z' && event.shiftKey)) {
        if (!stackRef.current.canRedo()) return;
        event.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, undo, redo]);

  return {
    canUndo,
    canRedo,
    persistPaused,
    persistEndsAt,
    persistPausedRef,
    suppressGetRefreshRef,
    ignorePersistResultRef,
    undo,
    redo,
    notifyPersistCaughtUp,
    requestFlushNow,
    recordCanvasNow,
    beginLiveSnapshotAbsorb,
  };
}
